import {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useMemo,
	useState,
} from 'react';

import { LeavePageModal } from '../components/LeavePageModal';
import { planningTableMessages } from '../variables/planningTableMessages';

interface ExitComponentContextProps {
	requestExit: (action: VoidFunction) => void;
	setIsEditing: (isEditing: boolean) => void;
	isEditing: boolean;
}

const ExitComponentContext = createContext<
	ExitComponentContextProps | undefined
>(undefined);

export const ExitComponentProvider = ({
	children,
}: {
	children: ReactNode;
}) => {
	const [action, setAction] = useState<VoidFunction | undefined>(undefined);
	const [isEditing, setIsEditing] = useState(false);
	const [openModal, setOpenModal] = useState(false);

	const requestExit = (newAction: VoidFunction) => {
		if (isEditing) {
			setAction(() => newAction); // evita re-executar imediatamente
			setOpenModal(true);
		} else {
			newAction();
		}
	};

	const handleConfirm = useCallback(() => {
		setIsEditing(false);
		action?.();
		reset();
	}, [action]);

	const handleCancel = useCallback(() => {
		reset();
	}, []);

	const reset = () => {
		setAction(undefined);
		setOpenModal(false);
	};

	const modal = useMemo(
		() => (
			<LeavePageModal
				description={
					planningTableMessages.MSG_LEAVE_COMPONENT_DESCRIPTION
				}
				isOpen={openModal}
				onConfirm={handleConfirm}
				onCancel={handleCancel}
			/>
		),
		[openModal, handleConfirm, handleCancel]
	);

	return (
		<ExitComponentContext.Provider
			value={{ requestExit, setIsEditing, isEditing }}
		>
			{openModal && modal}
			{children}
		</ExitComponentContext.Provider>
	);
};

export const useExitComponent = () => {
	const context = useContext(ExitComponentContext);
	if (!context) {
		throw new Error(
			'useExitComponent deve ser usado dentro de ExitComponentProvider'
		);
	}
	return context;
};
